/**
 * Recording a Live to MP4, started and stopped from the studio.
 *
 * The file is written by Egress, which runs beside LiveKit and not beside the
 * API - so the backend never sees a filesystem, only an HTTP source it is
 * trusted to read. Everything a client is allowed to know about a recording
 * comes through here, and the file itself is only ever served by the API,
 * behind the same ownership check as the Live.
 */

/**
 * LiveKit's egress states, narrowed to the ones a director can act on.
 *
 * `starting` matters as its own state: headless Chrome has to boot, load the
 * compositing page and join the room, which takes seconds. A button that
 * claimed to be recording in that window would be lying at the one moment it
 * costs the most.
 */
export const RECORDING_STATES = [
  'idle',
  'starting',
  'recording',
  'stopping',
  'complete',
  'failed',
] as const;

export type RecordingState = (typeof RECORDING_STATES)[number];

export interface RecordingStatus {
  state: RecordingState;
  /** LiveKit's handle on the job. Null when nothing has ever been recorded. */
  egressId: string | null;
  /**
   * ISO 8601. When LiveKit accepted the job - and, measured on 2026-08-21,
   * **where the file actually begins**.
   *
   * The recording spans from the request, not from the moment Egress calls
   * itself active, so this is the timestamp a duration has to be counted from.
   * It looks like the wrong one and is not: see `startedAt`.
   */
  requestedAt: string | null;
  /**
   * ISO 8601. When Egress stamped the file's own result as started.
   *
   * This is when the job went **active**, which is later than the first frame
   * in the file. Kept because the gap between the two is worth seeing - it is
   * the dead head every recording carries - but it is not the length of
   * anything, and counting from it understates the file.
   */
  startedAt: string | null;
  /**
   * ISO 8601. When the file was closed, and null while one is being written.
   *
   * Carried so a finished recording can say how long it is. Without it the
   * studio could only ever count from `startedAt` to now, which for a
   * recording that stopped an hour ago is not a duration, it is an age.
   */
  endedAt: string | null;
  /** The finished file's size in bytes, once Egress has reported one. */
  sizeBytes: number | null;
  /**
   * Whether a finished file can be downloaded now.
   *
   * False while recording: Egress writes the container as it goes and the
   * moov atom lands at the end, so a file taken mid-session is not playable.
   */
  downloadable: boolean;
  /** Egress's own reason, when it failed. */
  error: string | null;
}

export const IDLE_RECORDING: RecordingStatus = {
  state: 'idle',
  egressId: null,
  requestedAt: null,
  startedAt: null,
  endedAt: null,
  sizeBytes: null,
  downloadable: false,
  error: null,
};

/** True while a job is running and a file is still being written to. */
export function isRecordingActive(status: RecordingStatus): boolean {
  return status.state === 'starting' || status.state === 'recording';
}

/**
 * True while Egress is doing something - including stopping.
 *
 * `stopping` is not active: nothing more is being recorded. But it is a state
 * that changes on its own within seconds, so anything that polls has to treat
 * it like the others or it reports a job as still ending long after Egress
 * finished the file.
 */
export function isRecordingInFlight(status: RecordingStatus): boolean {
  return isRecordingActive(status) || status.state === 'stopping';
}

/**
 * What the file is called, and the only name the API will serve.
 *
 * Derived from the Live rather than chosen, so a request can never name a path
 * - the client asks for "the recording of this Live" and the server works out
 * the rest. See the download route for why that matters.
 */
export function recordingFileName(liveId: string): string {
  return `${liveId}.mp4`;
}
